const API_BASE = 'http://localhost:3001';

// Fetch all creatives for a specific campaign
export const fetchCampaignCreatives = async (campaignId, userId, brandId, platform, dateRange) => {
    const params = new URLSearchParams({
        userId,
        brandId,
        platform,
        ...(dateRange?.startDate && { startDate: dateRange.startDate }),
        ...(dateRange?.endDate && { endDate: dateRange.endDate })
    });

    const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/creatives?${params}`);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to fetch campaign creatives');
    }

    return response.json();
};

// Analyze a campaign's creative portfolio with AI
export const analyzeCampaign = async (campaignId, userId, brandId, platform, creatives, campaign) => {
    const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            userId,
            brandId,
            platform,
            creatives,
            campaign
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to analyze campaign');
    }

    return response.json();
};

// Analyze a single creative (reuse existing endpoint)
export const analyzeCreative = async (creative, userId, brandId) => {
    const response = await fetch(`${API_BASE}/api/creatives/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            userId,
            brandId,
            imageUrl: creative.imageUrl || creative.thumbnailUrl,
            creativeId: creative.id,
            metrics: creative.metrics,
            adCopy: creative.adCopy,
            isVideo: creative.isVideo
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to analyze creative');
    }

    return response.json();
};

// Cache helpers
const CREATIVE_CACHE_KEY = 'campaign-creative-analysis-';
const CAMPAIGN_CACHE_KEY = 'campaign-analysis-';
const CACHE_EXPIRY_HOURS = 24;

export const getCachedCreativeAnalysis = (brandId, creativeId) => {
    const cacheKey = `${CREATIVE_CACHE_KEY}${brandId}`;
    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_EXPIRY_HOURS * 3600000) {
                return data[creativeId] || null;
            }
        }
    } catch (e) {
        console.warn('Failed to read creative analysis cache:', e);
    }
    return null;
};

export const setCachedCreativeAnalysis = (brandId, creativeId, analysis) => {
    const cacheKey = `${CREATIVE_CACHE_KEY}${brandId}`;
    try {
        let cached = { data: {}, timestamp: Date.now() };
        const existing = localStorage.getItem(cacheKey);
        if (existing) {
            const parsed = JSON.parse(existing);
            if (Date.now() - parsed.timestamp < CACHE_EXPIRY_HOURS * 3600000) {
                cached = parsed;
            }
        }
        cached.data[creativeId] = analysis;
        cached.timestamp = Date.now();
        localStorage.setItem(cacheKey, JSON.stringify(cached));
    } catch (e) {
        console.warn('Failed to write creative analysis cache:', e);
    }
};

export const getCachedCampaignAnalysis = (brandId, campaignId) => {
    const cacheKey = `${CAMPAIGN_CACHE_KEY}${brandId}`;
    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_EXPIRY_HOURS * 3600000) {
                return data[campaignId] || null;
            }
        }
    } catch (e) {
        console.warn('Failed to read campaign analysis cache:', e);
    }
    return null;
};

export const setCachedCampaignAnalysis = (brandId, campaignId, analysis) => {
    const cacheKey = `${CAMPAIGN_CACHE_KEY}${brandId}`;
    try {
        let cached = { data: {}, timestamp: Date.now() };
        const existing = localStorage.getItem(cacheKey);
        if (existing) {
            const parsed = JSON.parse(existing);
            if (Date.now() - parsed.timestamp < CACHE_EXPIRY_HOURS * 3600000) {
                cached = parsed;
            }
        }
        cached.data[campaignId] = analysis;
        cached.timestamp = Date.now();
        localStorage.setItem(cacheKey, JSON.stringify(cached));
    } catch (e) {
        console.warn('Failed to write campaign analysis cache:', e);
    }
};

export const clearAnalysisCache = (brandId) => {
    try {
        localStorage.removeItem(`${CREATIVE_CACHE_KEY}${brandId}`);
        localStorage.removeItem(`${CAMPAIGN_CACHE_KEY}${brandId}`);
    } catch (e) {
        console.warn('Failed to clear analysis cache:', e);
    }
};

// Fetch aggregated placement statistics across all campaigns for a platform
export const fetchPlacementStats = async (userId, brandId, platform, dateRange) => {
    const params = new URLSearchParams({
        userId,
        brandId,
        platform,
        ...(dateRange?.startDate && { startDate: dateRange.startDate }),
        ...(dateRange?.endDate && { endDate: dateRange.endDate })
    });

    const response = await fetch(`${API_BASE}/api/campaigns/placement-stats?${params}`);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to fetch placement stats');
    }

    return response.json();
};

// Placement stats cache helpers
const PLACEMENT_CACHE_KEY = 'placement-stats-';
const PLACEMENT_CACHE_EXPIRY_HOURS = 1; // Cache placement stats for 1 hour

export const getCachedPlacementStats = (brandId, platform, dateRange) => {
    const dateKey = dateRange ? `${dateRange.startDate}-${dateRange.endDate}` : 'all';
    const cacheKey = `${PLACEMENT_CACHE_KEY}${brandId}-${platform}-${dateKey}`;
    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < PLACEMENT_CACHE_EXPIRY_HOURS * 3600000) {
                return data;
            }
        }
    } catch (e) {
        console.warn('Failed to read placement stats cache:', e);
    }
    return null;
};

export const setCachedPlacementStats = (brandId, platform, stats, dateRange) => {
    const dateKey = dateRange ? `${dateRange.startDate}-${dateRange.endDate}` : 'all';
    const cacheKey = `${PLACEMENT_CACHE_KEY}${brandId}-${platform}-${dateKey}`;
    try {
        localStorage.setItem(cacheKey, JSON.stringify({
            data: stats,
            timestamp: Date.now()
        }));
    } catch (e) {
        console.warn('Failed to write placement stats cache:', e);
    }
};
